/**
 * Authentification `/auth` : connexion (email / téléphone + OTP), inscription, Google.
 * `AuthPage.tsx` réexporte ce module pour conserver l’import existant dans l’app.
 */
import { useState, useEffect, type CSSProperties } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import Button from "@/components/ui/Button";

const loginSchema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "8 caractères minimum"),
});

const registerSchema = z
  .object({
    fullName: z.string().min(2, "Nom requis"),
    email: z.string().email("Email invalide"),
    password: z.string().min(8, "8 caractères minimum"),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
  });

const phoneSchema = z.object({
  phone: z.string().min(8, "Numéro invalide").regex(/^\+?[0-9]+$/, "Format invalide"),
});

const otpSchema = z.object({
  otp: z.string().length(6, "Code à 6 chiffres"),
});

type LoginForm = z.infer<typeof loginSchema>;
type RegisterForm = z.infer<typeof registerSchema>;
type PhoneForm = z.infer<typeof phoneSchema>;
type OtpForm = z.infer<typeof otpSchema>;

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [authMethod, setAuthMethod] = useState<"email" | "phone">("email");

  useEffect(() => {
    if (searchParams.get("inscription") === "1") setMode("register");
  }, [searchParams]);
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");

  const loginForm = useForm<LoginForm>({ resolver: zodResolver(loginSchema) });
  const registerForm = useForm<RegisterForm>({ resolver: zodResolver(registerSchema) });
  const phoneForm = useForm<PhoneForm>({ resolver: zodResolver(phoneSchema) });
  const otpForm = useForm<OtpForm>({ resolver: zodResolver(otpSchema) });

  const showMessage = (type: "success" | "error", text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const handleLogin = async (data: LoginForm) => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });
    setLoading(false);
    if (error) showMessage("error", error.message);
    else navigate("/dashboard");
  };

  const handleRegister = async (data: RegisterForm) => {
    setLoading(true);
    const { data: signData, error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: { full_name: data.fullName },
        emailRedirectTo: `${window.location.origin}/auth`,
      },
    });
    setLoading(false);
    if (error) {
      const msg = /already registered|User already registered|already been registered/i.test(error.message)
        ? "Si cette adresse est valide, un message vous a été envoyé (vérifiez aussi les courriers indésirables)."
        : error.message;
      showMessage("error", msg);
      return;
    }
    if (signData.session) {
      showMessage("success", "Compte créé.");
      navigate("/dashboard");
      return;
    }
    showMessage(
      "success",
      "Si cette adresse est valide, un email de confirmation vient de vous être envoyé. Ouvrez-le pour activer votre compte.",
    );
  };

  const handleGoogle = async () => {
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) {
      setLoading(false);
      showMessage("error", error.message);
    }
  };

  const handleSendOtp = async (data: PhoneForm) => {
    setLoading(true);
    const phone = data.phone.startsWith("+") ? data.phone : `+${data.phone}`;
    const { error } = await supabase.auth.signInWithOtp({ phone });
    setLoading(false);
    if (error) showMessage("error", error.message);
    else {
      setPhoneNumber(phone);
      setOtpSent(true);
      showMessage("success", "Code OTP envoyé !");
    }
  };

  const handleVerifyOtp = async (data: OtpForm) => {
    setLoading(true);
    const { error } = await supabase.auth.verifyOtp({
      phone: phoneNumber,
      token: data.otp,
      type: "sms",
    });
    setLoading(false);
    if (error) showMessage("error", error.message);
    else navigate("/dashboard");
  };

  const inputStyle: CSSProperties = {
    width: "100%",
    background: "#0A0A08",
    border: "1px solid #2A2520",
    borderRadius: "10px",
    padding: "12px 16px",
    color: "#F5F0E8",
    fontSize: "0.9rem",
    fontFamily: "'Sora', sans-serif",
    outline: "none",
    transition: "border-color 0.2s",
  };

  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: "0.78rem",
    color: "#9A8F7E",
    marginBottom: "6px",
    fontWeight: 600,
    letterSpacing: "0.03em",
  };

  const errorStyle: CSSProperties = {
    fontSize: "0.75rem",
    color: "#EF4444",
    marginTop: "4px",
  };

  return (
    <div
      style={{
        fontFamily: "'Sora', 'Segoe UI', sans-serif",
        background: "#0A0A0A",
        minHeight: "100vh",
        display: "flex",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;600;700;800&family=Space+Mono:wght@400;700&display=swap');
        * { box-sizing: border-box; }
        input:focus { border-color: #F97316 !important; }
        input::placeholder { color: #3A3020; }
        .tab-btn {
          flex: 1;
          padding: 10px;
          border: none;
          border-radius: 8px;
          font-family: 'Sora', sans-serif;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .method-btn {
          flex: 1;
          padding: 9px;
          border-radius: 8px;
          font-family: 'Sora', sans-serif;
          font-size: 0.8rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          border: 1px solid #2A2520;
        }
      `}</style>

      <div
        style={{
          flex: "0 0 45%",
          background: "linear-gradient(135deg, #0F0D06 0%, #1A1408 100%)",
          borderRight: "1px solid #1E1C14",
          padding: "60px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(249,115,22,0.1) 0%, transparent 70%)",
            top: "-100px",
            left: "-100px",
            pointerEvents: "none",
          }}
        />

        <svg
          style={{ position: "absolute", bottom: "10%", right: "-50px", opacity: 0.06 }}
          width="400"
          height="400"
          viewBox="0 0 400 400"
        >
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <line
              key={i}
              x1="200"
              y1="200"
              x2={200 + 180 * Math.cos((angle * Math.PI) / 180)}
              y2={200 + 180 * Math.sin((angle * Math.PI) / 180)}
              stroke="#F97316"
              strokeWidth="1"
            />
          ))}
          <circle cx="200" cy="200" r="180" fill="none" stroke="#F97316" strokeWidth="1" />
          <circle cx="200" cy="200" r="120" fill="none" stroke="#EAB308" strokeWidth="0.5" />
          <circle cx="200" cy="200" r="60" fill="none" stroke="#F97316" strokeWidth="0.5" />
        </svg>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", position: "relative" }}>
          <div
            style={{
              width: 38,
              height: 38,
              background: "linear-gradient(135deg, #F97316, #EAB308)",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "1.1rem",
              color: "#0A0A0A",
            }}
          >
            W
          </div>
          <span style={{ fontWeight: 800, fontSize: "1.2rem", color: "#F5F0E8", letterSpacing: "-0.02em" }}>
            Woahda<span style={{ color: "#F97316" }}>.</span>
          </span>
        </div>

        <div style={{ position: "relative" }}>
          <div
            style={{
              fontSize: "0.72rem",
              color: "#F97316",
              fontWeight: 700,
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              marginBottom: "20px",
              fontFamily: "'Space Mono', monospace",
            }}
          >
            SYSCOHADA RÉVISÉ 2017
          </div>
          <h1
            style={{
              fontSize: "2.8rem",
              fontWeight: 800,
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
              color: "#F5F0E8",
              marginBottom: "20px",
            }}
          >
            La compta{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #F97316, #EAB308)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              africaine
            </span>
            <br />
            réinventée.
          </h1>
          <p style={{ color: "#5A5040", fontSize: "0.95rem", lineHeight: 1.7, maxWidth: "360px" }}>
            Gérez votre comptabilité OHADA, synchronisez vos transactions Mobile Money et préparez vos déclarations fiscales en toute sérénité.
          </p>
        </div>

        <div style={{ position: "relative" }}>
          {[
            { icon: "📱", text: "Sync Orange Money, MTN & Wave" },
            { icon: "📊", text: "Bilan & Compte de résultat automatiques" },
            { icon: "🔒", text: "Données chiffrées et sécurisées" },
          ].map((f, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "12px",
                color: "#9A8F7E",
                fontSize: "0.85rem",
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  background: "#1A1710",
                  border: "1px solid #2A2520",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.9rem",
                  flexShrink: 0,
                }}
              >
                {f.icon}
              </span>
              {f.text}
            </div>
          ))}
        </div>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px",
        }}
      >
        <div style={{ width: "100%", maxWidth: "420px" }}>
          {message && (
            <div
              style={{
                background: message.type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
                border: `1px solid ${message.type === "success" ? "#22C55E40" : "#EF444440"}`,
                borderRadius: "10px",
                padding: "12px 16px",
                fontSize: "0.85rem",
                color: message.type === "success" ? "#22C55E" : "#EF4444",
                marginBottom: "20px",
              }}
            >
              {message.type === "success" ? "✓" : "⚠"} {message.text}
            </div>
          )}

          <div
            style={{
              display: "flex",
              background: "#111109",
              border: "1px solid #1E1C14",
              borderRadius: "12px",
              padding: "4px",
              marginBottom: "28px",
            }}
          >
            {(["login", "register"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                className="tab-btn"
                onClick={() => {
                  setMode(tab);
                  setMessage(null);
                }}
                style={{
                  background: mode === tab ? "linear-gradient(135deg, #F97316, #EAB308)" : "transparent",
                  color: mode === tab ? "#0A0A0A" : "#5A5040",
                }}
              >
                {tab === "login" ? "Connexion" : "Créer un compte"}
              </button>
            ))}
          </div>

          {mode === "login" && (
            <div style={{ display: "flex", gap: "8px", marginBottom: "24px" }}>
              {(["email", "phone"] as const).map((method) => (
                <button
                  key={method}
                  type="button"
                  className="method-btn"
                  onClick={() => {
                    setAuthMethod(method);
                    setOtpSent(false);
                    setMessage(null);
                  }}
                  style={{
                    background: authMethod === method ? "#1A1710" : "transparent",
                    color: authMethod === method ? "#F97316" : "#5A5040",
                    borderColor: authMethod === method ? "#F97316" : "#2A2520",
                  }}
                >
                  {method === "email" ? "📧 Email" : "📱 Téléphone"}
                </button>
              ))}
            </div>
          )}

          {mode === "login" && authMethod === "email" && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)}>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Adresse email</label>
                <input {...loginForm.register("email")} style={inputStyle} placeholder="vous@entreprise.com" type="email" />
                {loginForm.formState.errors.email && (
                  <p style={errorStyle}>{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={labelStyle}>Mot de passe</label>
                <input {...loginForm.register("password")} style={inputStyle} placeholder="••••••••" type="password" />
                {loginForm.formState.errors.password && (
                  <p style={errorStyle}>{loginForm.formState.errors.password.message}</p>
                )}
                <div style={{ textAlign: "right", marginTop: "6px" }}>
                  <span style={{ fontSize: "0.78rem", color: "#F97316", cursor: "pointer" }}>Mot de passe oublié ?</span>
                </div>
              </div>

              <Button type="submit" variant="primary" loading={loading} className="w-full">
                Se connecter →
              </Button>
            </form>
          )}

          {mode === "login" && authMethod === "phone" && (
            <>
              {!otpSent ? (
                <form onSubmit={phoneForm.handleSubmit(handleSendOtp)}>
                  <div style={{ marginBottom: "24px" }}>
                    <label style={labelStyle}>Numéro de téléphone</label>
                    <input {...phoneForm.register("phone")} style={inputStyle} placeholder="+225 07 00 00 00 00" type="tel" />
                    {phoneForm.formState.errors.phone && (
                      <p style={errorStyle}>{phoneForm.formState.errors.phone.message}</p>
                    )}
                    <p style={{ fontSize: "0.75rem", color: "#5A5040", marginTop: "6px" }}>
                      Format international requis (ex: +225...)
                    </p>
                  </div>
                  <Button type="submit" variant="primary" loading={loading} className="w-full">
                    Envoyer le code OTP →
                  </Button>
                </form>
              ) : (
                <form onSubmit={otpForm.handleSubmit(handleVerifyOtp)}>
                  <div style={{ marginBottom: "24px" }}>
                    <label style={labelStyle}>Code OTP reçu par SMS</label>
                    <input
                      {...otpForm.register("otp")}
                      style={{
                        ...inputStyle,
                        letterSpacing: "0.3em",
                        textAlign: "center",
                        fontSize: "1.4rem",
                        fontFamily: "'Space Mono', monospace",
                      }}
                      placeholder="000000"
                      maxLength={6}
                      type="text"
                    />
                    {otpForm.formState.errors.otp && <p style={errorStyle}>{otpForm.formState.errors.otp.message}</p>}
                    <p style={{ fontSize: "0.75rem", color: "#5A5040", marginTop: "6px" }}>Code envoyé au {phoneNumber}</p>
                  </div>
                  <Button type="submit" variant="primary" loading={loading} className="w-full">
                    Vérifier le code →
                  </Button>
                  <button
                    type="button"
                    onClick={() => setOtpSent(false)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#5A5040",
                      fontSize: "0.8rem",
                      cursor: "pointer",
                      marginTop: "12px",
                      width: "100%",
                      fontFamily: "'Sora', sans-serif",
                    }}
                  >
                    ← Changer de numéro
                  </button>
                </form>
              )}
            </>
          )}

          {mode === "register" && (
            <form onSubmit={registerForm.handleSubmit(handleRegister)}>
              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Nom complet</label>
                <input {...registerForm.register("fullName")} style={inputStyle} placeholder="Koné Amadou" />
                {registerForm.formState.errors.fullName && (
                  <p style={errorStyle}>{registerForm.formState.errors.fullName.message}</p>
                )}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Adresse email</label>
                <input {...registerForm.register("email")} style={inputStyle} placeholder="vous@entreprise.com" type="email" />
                {registerForm.formState.errors.email && (
                  <p style={errorStyle}>{registerForm.formState.errors.email.message}</p>
                )}
              </div>

              <div style={{ marginBottom: "16px" }}>
                <label style={labelStyle}>Mot de passe</label>
                <input {...registerForm.register("password")} style={inputStyle} placeholder="••••••••" type="password" />
                {registerForm.formState.errors.password && (
                  <p style={errorStyle}>{registerForm.formState.errors.password.message}</p>
                )}
              </div>

              <div style={{ marginBottom: "24px" }}>
                <label style={labelStyle}>Confirmer le mot de passe</label>
                <input {...registerForm.register("confirmPassword")} style={inputStyle} placeholder="••••••••" type="password" />
                {registerForm.formState.errors.confirmPassword && (
                  <p style={errorStyle}>{registerForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <Button type="submit" variant="primary" loading={loading} className="w-full">
                Créer mon compte →
              </Button>
            </form>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "20px 0" }}>
            <div style={{ flex: 1, height: "1px", background: "#1E1C14" }} />
            <span style={{ fontSize: "0.75rem", color: "#3A3020" }}>ou continuer avec</span>
            <div style={{ flex: 1, height: "1px", background: "#1E1C14" }} />
          </div>

          <Button
            type="button"
            variant="ghost"
            loading={loading}
            onClick={handleGoogle}
            className="w-full !border-[#2A2520] !bg-[#111109] !text-[#F5F0E8] hover:!border-orange-500 hover:!bg-[#161612] hover:!text-orange-500"
            icon={
              <svg width="18" height="18" viewBox="0 0 18 18" className="shrink-0" aria-hidden>
                <path
                  fill="#4285F4"
                  d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 002.38-5.88c0-.57-.05-.66-.15-1.18z"
                />
                <path
                  fill="#34A853"
                  d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 01-7.18-2.54H1.83v2.07A8 8 0 008.98 17z"
                />
                <path
                  fill="#FBBC05"
                  d="M4.5 10.52a4.8 4.8 0 010-3.04V5.41H1.83a8 8 0 000 7.18l2.67-2.07z"
                />
                <path
                  fill="#EA4335"
                  d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 001.83 5.4L4.5 7.49a4.77 4.77 0 014.48-3.31z"
                />
              </svg>
            }
          >
            Continuer avec Google
          </Button>

          <p
            style={{
              textAlign: "center",
              fontSize: "0.75rem",
              color: "#3A3020",
              marginTop: "24px",
              lineHeight: 1.6,
            }}
          >
            En vous connectant, vous acceptez nos <span style={{ color: "#F97316", cursor: "pointer" }}>CGU</span> et
            notre <span style={{ color: "#F97316", cursor: "pointer" }}>politique de confidentialité</span>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
